import { useAuth } from '@app/contexts/auth';
import { SnackbarLocationState, useSnackbar } from '@app/contexts/snackbar';
import { LoginContacts, LoginStep, SendTo } from '@app/pages/auth/Login/Login';
import { useStep2 } from '@app/pages/auth/Login/useStep2';
import { UserRoles } from '@app/pages/users/types';
import { getErrorMessage } from '@app/utils/http/getErrorMessage';
import { authService } from '@app/utils/services/authService';
import { credentialsService } from '@app/utils/services/credentialsService';
import { AxiosError } from 'axios';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import { useHistory, useLocation } from 'react-router';
import { State } from './useStep1';

export const useStep3 = (
  method: SendTo,
  email: string,
  phone: string,
  setStep: Dispatch<SetStateAction<LoginStep>>,
  setSendTo: Dispatch<SetStateAction<SendTo>>,
  setContacts: Dispatch<SetStateAction<LoginContacts>>
) => {
  const { open, close } = useSnackbar();
  const history = useHistory();
  const { state } = useLocation<SnackbarLocationState & State>();
  const { setIsAuthorized } = useAuth();
  const initialCounter = 120;
  const [counter, setCounter] = useState(initialCounter);
  const [code, setCode] = useState('');

  const { send: resendCode } = useStep2(method, setSendTo, email, phone, setStep, setContacts);

  useEffect(() => {
    if (counter <= 0) {
      return;
    }
    const timeout = setTimeout(() => {
      setCounter((counter) => counter - 1);
    }, 1000);

    return () => {
      timeout && clearTimeout(timeout);
    };
  }, [counter, setCounter]);

  useEffect(() => {
    close();
  }, [code]);

  const { mutate, isLoading } = useMutation(authService.verifyCode, {
    onSuccess: ({ token, expiredAt, roleId }) => {
      credentialsService.set({ token, expiredAt, roleId });
      setIsAuthorized(true);
      const pathname =
        roleId === UserRoles.Receiver ? '/receiver-terminal' : state?.backlink ? state?.backlink : '/dashboard';
      const { backLocation } = state || { backLocation: undefined };
      history.push({ pathname, state: { backLocation } });
    },
    onError: (error: AxiosError) => {
      open({ severity: 'error', message: getErrorMessage(error) });
    },
  });

  const send = () => {
    const timeOffset = -new Date().getTimezoneOffset();
    close();
    mutate({ email, code, timeOffset });
  };

  const resend = () => {
    setCounter(initialCounter);
    resendCode();
  };

  return {
    code,
    setCode,
    isLoading,
    send,
    resend,
    counter,
  };
};
